<template>
  <div class="pages-ccd-tenancy-details">
    <TenancyHeader
      :tenancyId="currentTenancy.id"
    >
      <Sticky
        extendStickyClass="sticky-button--group"
        :fullWidth="false"
        :zIndex="Number(101)"
        :align="'right'"
      >
        <button
          v-if="!isCreate"
          v-hkldreset
          type="button"
          class="btn btn--darkblue"
          @click="cancelEdit()"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          v-if="!isCreate"
          v-hkldreset
          type="button"
          class="btn btn--orange"
          @click="companyNameCheckBeforeSaving()"
        >
          {{ $t('action.save') }}
        </button>
        <button
          v-if="isCreate"
          v-hkldreset
          type="button"
          class="btn btn--orange"
          @click="create()"
        >
          {{ $t('action.create') }}
        </button>
      </Sticky>
    </TenancyHeader>

    <div class="tabs-container-wrapper">
      <TabsContainer
        :tabNames="tabNames"
        class="tabs-container"
        :getCurrentTab="getCurrentTab"
      >
        <TenancyInformation
          ref="tenancyInfo"
          :slot="`tab-${tenancyEditTabs.tenancyInformation}`"
          :company="companyInfo"
          :companyId="companyId"
          :originLeases="keep.leases || []"
          :originPremises="keep.premises || ''"
          :isCreate="isCreate"
          :allowEdit="allowEditInfo"
          :allowLinkContacts="allowLinkContacts"
          :allowUpdateMasterAccount="allowUpdateMasterAccount"
        />
        <div
          v-if="!isCreate"
          :slot="`tab-${tenancyEditTabs.allContacts}`"
        >
          <TenantConfirm />
          <ECircularContact
            ref="eCircularContact"
            :companyId="companyInfoId"
            :tenancyId="tenancyId"
            :allowLinkContacts="allowLinkContacts"
            :linkFunc="goToLinkContact"
          />
          <EmergencyContact
            ref="emergencyContact"
            :companyId="companyInfoId"
            :tenancyId="tenancyId"
            :allowLinkContacts="allowLinkContacts"
            :linkFunc="goToLinkContact"
          />
          <FireMarshalContact
            ref="fireMarshalContact"
            :allowLinkContacts="allowLinkContacts"
            :linkFunc="goToLinkContact"
          />
          <MarketingContact
            v-if="isRetail"
            ref="marketingContact"
            :companyId="companyInfoId"
            :tenancyId="tenancyId"
            :allowLinkContacts="allowLinkContacts && canEditMarketing"
            :linkFunc="goToLinkContact"
          />
          <OthersContact
            :companyId="companyInfoId"
            :tenancyId="tenancyId"
            :allowLinkContacts="allowLinkContacts"
            :linkFunc="goToLinkContact"
          />
        </div>
        <LeaseInformation
          v-if="!isCreate"
          :slot="`tab-${tenancyEditTabs.leaseInformation}`"
          :isEdit="allowLinkLeases"
        />
        <UserManagement
          v-if="!isCreate"
          ref="userManagement"
          :slot="`tab-${tenancyEditTabs.userManagement}`"
          :isEdit="true"
          :allowUpdateMasterAccount="allowUpdateMasterAccount"
          :allowInviteMaster="allowInviteMaster"
        />
      </TabsContainer>
    </div>
    <ActionConfirmModal
      ref="renameConfirmModal"
      :modalId="'renameConfirmModalId'"
      :confirmTitle="$t('tenancyRecords.confirm.companyNameCheckBeforeSaving')"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { provide, computed, unref, getCurrentInstance } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import ECircularContact from '@/modules/ccd/tenancy/edit/ECircularContact.vue'
import EmergencyContact from '@/modules/ccd/tenancy/edit/EmergencyContact.vue'
import LeaseInformation from '@/modules/ccd/tenancy/edit/LeaseInformation.vue'
import MarketingContact from '@/modules/ccd/tenancy/edit/MarketingContact.vue'
import OthersContact from '@/modules/ccd/tenancy/edit/OthersContact'
import Sticky from '@/controls/Sticky.vue'
import TabsContainer from '@/controls/TabsContainer.vue'
import TenancyHeader from '@/modules/ccd/tenancy/TenancyHeader.vue'
import TenancyInformation from '@/modules/ccd/tenancy/edit/TenancyInformation.vue'
import UserManagement from '@/pages/ccd/UserManagement.vue'
import TenantConfirm from '@/pages/ccd/TenantConfirm.vue'
import EventBus from '@/utils/eventBus'
import mutationTypes from '@/store/mutation-types'
import i18n from '@/utils/i18n'
import navigation from '@/utils/navigation'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { ccdRouteNames, tenancyEditTabs, tenancyTabsEditToView } from '@/utils/consts'
import { buildContactRequest } from '@/modules/ccd/utils'
import { tenancyType } from '@/utils/locale'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import FireMarshalContact from '@/modules/ccd/tenancy/edit/FireMarshalContact.vue'
import { updateTenancyContacts } from '@/api/tenancies/request'
import { TR_PROVIDE_KEYS } from './tenancy.consts'

export default {
  name: 'TenancyDetail',
  components: {
    TenancyHeader,
    TenancyInformation,
    TabsContainer,
    ECircularContact,
    EmergencyContact,
    FireMarshalContact,
    MarketingContact,
    OthersContact,
    LeaseInformation,
    UserManagement,
    Sticky,
    ActionConfirmModal,
    TenantConfirm,
  },
  beforeRouteLeave (to, from, next) {
    const tenancyViewRoutes = [
      ccdRouteNames.ccdTenancyView,
      ccdRouteNames.ccdTenancyTenantView,
    ]
    if (!tenancyViewRoutes.includes(to.name)) {
      this.$store.dispatch('listNavigation/clearTenancyBackPath')
    }
    next()
  },
  setup () {
    provide(TR_PROVIDE_KEYS.DETAIL_EDITING, true)
    const { proxy } = getCurrentInstance()
    const companyInfo = computed(() => {
      return proxy.company
    })
    const companyInfoId = computed(() => {
      return lodash.get(unref(companyInfo), 'id')
    })
    return {
      companyInfo,
      companyInfoId,
    }
  },
  data () {
    return {
      companyId: null,
      inputRequireHighlight: false,
      isEditable: false,
      tenancyId: null,
      company: null,
      keep: {},
      tenancyEditTabs,
      currentTabIndex: 0,
      routeQueryTabName: null,
      privateTabNames: [],
    }
  },
  computed: {
    ...mapGetters('ccd', ['currentCompany', 'currentTenancy']),
    ...mapGetters('user', ['acTenancyIsOffice', 'acTenancyIsRetail', 'isTenant', 'isEngineer']),
    ...mapGetters('regions', ['enableTenantProjectsFeature', 'enableWorkPermitsFeature', 'enableGreenOperationFeature']),
    ...mapGetters('form', ['isPageDirty']),
    infoTabName () {
      return i18n.t('tenancyView.tenancyInformation')
    },
    editTabNames () {
      return [
        i18n.t('tenancyView.allContacts'),
        i18n.t('lease.linkedLeases'),
        i18n.t('tenancyView.userManagement'),
      ]
    },
    tabNames: {
      get () {
        return lodash.isEmpty(this.privateTabNames) ? [this.infoTabName, ...this.editTabNames] : this.privateTabNames
      },
      set (val) {
        this.privateTabNames = val
      },
    },
    isCreate () {
      return !this.tenancyId
    },
    allowEditInfo () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.update)
    },
    canEditMarketing () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.manageMarketingContacts)
    },
    isRetail () {
      return this.currentTenancy.type === tenancyType.Retail
    },
    allowLinkLeases () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.linkLeases)
    },
    allowLinkContacts () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.linkContacts)
    },
    allowInviteMaster () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.inviteMaster)
    },
    allowUpdateMasterAccount () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.updateTenancyMasterAccount)
    },
  },
  watch: {
    isCreate () {
      this.setupTabNames()
    },
  },
  async mounted () {
    const routeParams = this.$router.currentRoute.params
    this.tenancyId = routeParams.id
    this.setupTabNames()
    this.companyId = this.$router.currentRoute.query.companyId
    const tabIndex = routeParams.tabIndex
    if (tabIndex) {
      this.getCurrentTab(tabIndex)
    }
    // I don't understand why here replace tabIndex of URL. I think that it is not necessary operation
    if (this.$router.currentRoute.query.tabIndex) {
      this.$router.replace({
        path: this.$router.currentRoute.path,
        query: {
          companyId: this.companyId,
        },
      })
    }
    const routeName = this.$router.currentRoute.name
    if (routeName === ccdRouteNames.ccdTenancyEdit) {
      if (this.companyId) {
        this.company = this.currentTenancy.company
      }
      await this.loadTenancy(this.tenancyId)
      this.keep = lodash.cloneDeep(this.currentTenancy)
      this.$refs.eCircularContact.removeInvalidFloors()
      await this.addTenancyContactsToStore(routeParams)
    } else {
      this.resetCurrentTenancy({'type': this.acTenancyIsOffice ? 'Office' : this.acTenancyIsRetail ? 'Retail' : ''})
      if (this.companyId) {
        this.company = this.currentCompany
      }
    }
  },
  methods: {
    ...mapActions('ccd', [
      'loadTenancy',
      'createTenancy',
      'updateTenancy',
      'updateOpeningHours',
      'addTenancyContactsToStore',
      'removeTenancyContacts',
      'linkLeasesToTenancy',
      'removeTenancyLeases',
      'getCompanyAllLeases',
    ]),
    ...mapMutations('ccd', {
      updateTenancyPathField: mutationTypes.TENANCY_UPDATE_PATH_FIELD,
      resetCurrentTenancy: mutationTypes.TENANCY_RESET_CURRENT,
    }),
    getCurrentTab (index) {
      this.currentTabIndex = index
    },
    async create () {
      if (!this.$refs.tenancyInfo.createValidator()) {
        return false
      }
      if (!this.currentTenancy.email) {
        delete this.currentTenancy.email
      }
      const tenancy = await this.createTenancy()
      if (tenancy.id) {
        this.$router.replace({
          name: ccdRouteNames.ccdTenancyView,
          params: { id: this.currentTenancy.id },
        })
      }
    },
    buildLeasesRequest (leases) {
      const linkLeasesRequest = []
      const removeLeasesRequest = []
      if (Array.isArray(leases)) {
        const keptLeases = this.keep.leases.map(item => item.id)
        const newLeases = leases.map(item => item.id)
        newLeases.forEach(item => {
          if (!keptLeases.includes(item)) {
            linkLeasesRequest.push(item)
          }
        })
        keptLeases.forEach(item => {
          if (!newLeases.includes(item)) {
            removeLeasesRequest.push(item)
          }
        })
      }
      return [linkLeasesRequest, removeLeasesRequest]
    },
    async linkLeases () {
      const [linkLeasesRequest, removeLeasesRequest] = this.buildLeasesRequest(this.currentTenancy.leases)
      if (linkLeasesRequest.length) {
        await this.linkLeasesToTenancy({
          tenancyId: this.currentTenancy.id,
          request: { leaseIds: linkLeasesRequest },
        })
      }
      if (removeLeasesRequest.length) {
        await this.removeTenancyLeases({
          tenancyId: this.currentTenancy.id,
          request: { leaseIds: removeLeasesRequest },
        })
      }
    },
    assignTradeName () {
      if (!this.currentTenancy.tradeName && this.currentTenancy.leases.length > 0) {
        this.updateTenancyPathField({
          path: ['tradeName'],
          newValue: this.currentTenancy.leases[0].tradeName,
        })
      }
    },
    async saveAllTabs () {
      fusionInputCheckUtil.resetAllFields()
      const accountsInvalid = !this.$refs.userManagement.validate()
      const infoInvalid = !this.$refs.tenancyInfo.saveValidator()
      const eCircularInvalid = this.$refs.eCircularContact.isInvalidFloorEmails()
      const fireMarshalInvalid = !this.$refs.fireMarshalContact.validate()
      const emergencyInvalid = this.$refs.emergencyContact.isInvalid()
      let marketingInvalid = false
      // Only For hkl user, should have marketing perm to edit
      if (this.isRetail && this.canEditMarketing) {
        marketingInvalid = this.$refs.marketingContact.isInvalidEmailsTypes()
      }
      const allContactsTabInvalid = eCircularInvalid || emergencyInvalid || fireMarshalInvalid || marketingInvalid
      if (allContactsTabInvalid) {
        EventBus.emit('common-tab-error-highlight', ['tab-1'])
      }
      if (infoInvalid || accountsInvalid || allContactsTabInvalid) {
        return false
      }

      this.assignTradeName()

      if (this.allowEditInfo) {
        const isUpdated = await this.updateTenancy()
        await this.updateOpeningHours({tenancyId: this.currentTenancy.id, request: this.currentTenancy.openingHours, isSilentUpdate: true})

        if (!isUpdated) {
          return false
        }
      }

      if (this.allowLinkContacts) {
        const updateContactRequest =
          buildContactRequest(this.currentTenancy.tenancyContacts, this.keep.tenancyContacts, this.keep.companyId)
        if (updateContactRequest.length) {
          await updateTenancyContacts(
            this.currentTenancy.id,
            updateContactRequest,
          )
        }
      }

      if (this.allowLinkLeases) {
        await this.linkLeases()
      }

      if (this.allowInviteMaster || this.allowUpdateMasterAccount) {
        await this.$refs.userManagement.saveMasterContact()
      }
      return true
    },
    compareDummyCompanyName (prev, current) {
      // Distinct dummy company by searching name in db, ignore upper/lower case
      return (prev || '').toLowerCase() !== (current || '').toLowerCase()
    },
    async companyNameCheckBeforeSaving () {
      const originalCompany = lodash.get(this.keep, 'company')
      const currentCompany = lodash.get(this.currentTenancy, 'company')
      const isTypeDiff = currentCompany.type !== originalCompany.type
      if (isTypeDiff || (currentCompany.id && currentCompany.id !== originalCompany.id) || this.compareDummyCompanyName(originalCompany.name, currentCompany.name)) {
        this.$refs.renameConfirmModal.open(this.save, [])
      } else {
        this.save()
      }
    },
    goToViewPage () {
      const tabs = lodash.cloneDeep(tenancyTabsEditToView)
      const moduleConfigTabsForLeases = [this.enableTenantProjectsFeature, this.enableGreenOperationFeature]
      const enabledTabsForLeases = moduleConfigTabsForLeases.filter(Boolean)
      tabs.push(tabs.length + enabledTabsForLeases.length)
      const moduleConfigTabs = [this.enableTenantProjectsFeature, this.enableGreenOperationFeature, this.enableWorkPermitsFeature]
      const enabledTabs = moduleConfigTabs.filter(Boolean)
      tabs.push(tabs.length + enabledTabs.length)
      const tabIndex = tabs[this.currentTabIndex]
      this.$router.replace({
        name: ccdRouteNames.ccdTenancyView,
        params: { id: this.tenancyId },
        query: { companyId: this.companyId, tabIndex },
      })
    },
    async save () {
      const isSaved = await this.saveAllTabs()
      if (isSaved) {
        formUtils.resetDirty()
        this.goToViewPage()
      }
    },
    cancelEdit () {
      formUtils.resetDirty()
      if (!this.currentTenancy.id) {
        this.$router.goBack()
        return false
      } else {
        this.goToViewPage()
      }
      this.inputRequireHighlight = false
    },
    setupTabNames () {
      if (this.isCreate) {
        this.tabNames = [this.infoTabName]
      } else {
        this.tabNames = [this.infoTabName, ...this.editTabNames]
      }
    },
    async goToLinkContact (params) {
      let valid = true
      if (this.isPageDirty) {
        valid = await this.saveAllTabs()
      }
      if (valid) {
        formUtils.resetDirty()
        navigation.toPage(this.isTenant ? 'tenantContacts' : 'contacts', {}, {
          tenancyId: this.currentTenancy.id,
          companyId: this.companyId,
          ...params,
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import "~styles/tabsContainer";

.pages-ccd-tenancy-details {
  height: 100%;
  background: #ebebeb;
  .tabs-container-wrapper {
    padding: 15px;
    height: calc(100% - 60px);
  }

  .sticky-button--group {
    > div {
      margin-right: 1%;
    }
  }

  .tabs-container {
    max-width: 1440px;
    margin: 0 auto;
    height: 100%;
    .hkld-tabs {
      width: 100%;
      .column {
        width: 250px;
        display: inline-block;
        flex: none;
        box-shadow: 0 2px 4px rgba(gray, .2);
        font-weight: bold;
      }
      .tabs-title>a {
        font-size: 16px;
        background-color: white;
        line-height: 24px;
      }
    }
    .common-tabs, .tabs-panel {
      background: white;
      padding: 20px 24px;
    }
    .create-project-main {
      height: 100%;
      .hkld-tabs-content {
        background-color: white;
        box-shadow: 0 2px 4px rgba(128, 128, 128, 0.2);
      }
    }
  }
  #sticky-tabs {
    width: 100%;
    .sticky-tabs-nav {
      a {
        font-weight: bold;
      }
    }
  }
}

.tenancy-reveal {
  padding: 0;
  .modal-container {
    padding: 30px;
    .confirm-header {
      font-weight: 550;
    }
  }
  .alert-btn-group {
    margin-top: 40px;
    .btn {
      width: 100%;
      height: 30px;
    }
    .btn:hover {
      background-color: #0C3A58;
      color: #fff;
    }
  }
}
</style>
